/**
 * The template's ecommerce dashboard,
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/dashboard/index.tsx — its `@container`
 * / `grid-cols-1 @4xl:grid-cols-2 @7xl:grid-cols-12` grid and its slot order: `WelcomeBanner` and
 * `StatCards` at eight columns beside a tall four-column widget, `SalesReport` beside a
 * four-column list, `RecentOrder` across the full width. Every widget in those slots is a Steelyard
 * tile fed by the API, never the template's constants (FR-020):
 *
 *  - StatCards → `OrderTrendCards` + `InvoicedCard` (`/reports/order-trend`, `/reports/sales-trend`)
 *  - ProfitWidget (tall) → `OrdersByStatusTile` (the pipeline, as `fleet-status.tsx`'s donut)
 *  - SalesReport → `InvoicedByMonthTile`; PromotionalSales → top SKUs; BestSellers → top customers
 *  - RepeatCustomerRate → the "needs attention" `MetricCard` grid (FR-001's remaining tiles)
 *  - RecentOrder → `PendingApprovalsTile`, with `PendingCustomersTile` (registrations pending
 *    verification) and `PendingInvitationsTile` (invited, not yet accepted) beside it
 *
 * Below the banner, slots are grouped into titled sections in the order an admin acts on them —
 * Waiting on you, Operations, Sales — rather than the template's single undivided run.
 *
 * `UserLocation` and `StockReport` have no slot: no geography and no reorder threshold exists in
 * any service. The dev-only Visual-QA section that used to render the template's demo widgets under
 * this grid is gone — no demo figure appears on this screen in any build.
 *
 * The `DashboardTile` contract (CLAUDE.md) is unchanged: one file per tile under
 * `components/dashboard/`, each fed a promise this loader starts and never awaits, each failing
 * independently through `TileBoundary` as "Unreadable", never as a coerced `0` (FR-002, FR-005).
 * Visibility is the permission matrix via `can()` — no role check anywhere (FR-004).
 */
import type { ReactNode } from "react";
import { Link, useLoaderData, useOutletContext, useRevalidator } from "react-router-dom";
import { PiArrowsClockwiseBold as Refresh, PiHardHatDuotone as Construction, PiPackageDuotone as Package, PiUsersDuotone as Users } from "react-icons/pi";
import { can } from "../lib/permissions";
import { BUSINESS_TIME_ZONE as ZONE } from "../lib/format";
import { pendingNav } from "../lib/nav";
import { getPermissionState } from "../lib/identity";
import type { AccountSummary, PermissionState } from "../types";
import { listOrders, getOrderPipeline, getOrderTrend, type OrderSummary, type OrderTrendReport, type PagedResult } from "../lib/services/ordering";
import { getAgentStatus, getSyncQueue, type AgentStatus } from "../lib/services/tallySync";
import { getStockExceptions, getVarianceReport, type VarianceReportView } from "../lib/services/warehouse";
import { getTierStalenessReport, type TierStalenessReport } from "../lib/services/catalog";
import { getOutstandingRefunds, type OutstandingRefund } from "../lib/services/finance";
import { getCreditBreaches, listCustomers, type PagedCustomers } from "../lib/services/customers";
import { listInvitations, type InvitationList } from "../lib/services/invitations";
import { getSalesReport, getSalesTrend, type SalesTrendReport } from "../lib/services/invoicing";
import { PendingApprovalsTile } from "../components/dashboard/PendingApprovalsTile";
import { OrdersByStatusTile } from "../components/dashboard/OrdersByStatusTile";
import { OrderTrendCards } from "../components/dashboard/OrderTrendCards";
import { InvoicedCard } from "../components/dashboard/InvoicedCard";
import { InvoicedByMonthTile } from "../components/dashboard/InvoicedByMonthTile";
import { TopSellersTile, type TopRow } from "../components/dashboard/TopSellersTile";
import { AwaitingApprovalCard } from "../components/dashboard/AwaitingApprovalCard";
import { StaleTierPricesTile } from "../components/dashboard/StaleTierPricesTile";
import { PackingVarianceTile } from "../components/dashboard/PackingVarianceTile";
import { StockExceptionsTile } from "../components/dashboard/StockExceptionsTile";
import { AgentHeartbeatTile } from "../components/dashboard/AgentHeartbeatTile";
import { FailedSyncsTile } from "../components/dashboard/FailedSyncsTile";
import { OutstandingRefundsTile } from "../components/dashboard/OutstandingRefundsTile";
import { CreditBreachTile } from "../components/dashboard/CreditBreachTile";
import { PendingCustomersTile } from "../components/dashboard/PendingCustomersTile";
import { PendingInvitationsTile } from "../components/dashboard/PendingInvitationsTile";
import { PageHeader, WelcomeBanner, HandWaveIcon, WidgetCard } from "@steelyard/theme";
import { Button } from "rizzui";
import { customerPath } from "../lib/paths";

/** A calendar date in the business zone, as the `DateOnly` the report routes take. */
const isoDay = (date: Date) => date.toLocaleDateString("en-CA", { timeZone: ZONE });

const TODAY = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: ZONE });
const UPDATED = new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: ZONE, timeZoneName: "short" });

/** The period every "last 30 days" figure on this screen covers, stated once so they all agree. */
const RECENT = "Last 30 days";

/**
 * Billing's sales report names each customer itself (`groupName`); one it could not name reads
 * "Unknown customer", never its id. `canName` (customers.read) only decides whether the row links.
 */
async function topCustomers(from: string, to: string, canName: boolean): Promise<TopRow[]> {
  const report = await getSalesReport({ from, to, groupBy: "customer", top: 5 });
  return report.rows.map((row) => ({
    ...row,
    groupName: row.groupName || "Unknown customer",
    href: canName && row.groupId ? customerPath(row.groupId) : undefined,
  }));
}

/**
 * T007/FR-005: every promise below is **started, not awaited** — the loader itself awaits only
 * `getPermissionState()`, which every tile's own gate depends on. A caller without a tile's
 * permission never even starts that tile's fetch (T012, FR-004) — its promise is `null` and the
 * tile is omitted from the grid entirely below, not rendered hidden.
 */
export async function dashboardLoader() {
  const permissionState = await getPermissionState();

  const now = new Date();
  const to = isoDay(now);
  const from30 = isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29));
  const from12m = isoDay(new Date(now.getFullYear(), now.getMonth() - 11, 1));
  const canReadOrders = can(permissionState, "orders.read");
  const canReadReports = can(permissionState, "billing.read");

  const ordersPromise = canReadOrders ? listOrders({ page: 1, state: "awaiting_approval" }) : null;
  const pipelinePromise = canReadOrders ? getOrderPipeline() : null;
  const orderTrendPromise = canReadOrders ? getOrderTrend({ from: from30, to, bucket: "day" }) : null;
  // The sales-trend and top-five reads are the same Billing report surface `/reports/sales` shows,
  // so they share its gate (nav.tsx).
  const invoicedDailyPromise = canReadReports ? getSalesTrend({ from: from30, to, bucket: "day" }) : null;
  const invoicedMonthlyPromise = canReadReports ? getSalesTrend({ from: from12m, to, bucket: "month" }) : null;
  const topSkusPromise = canReadReports ? getSalesReport({ from: from30, to, groupBy: "sku", top: 5 }).then((r): TopRow[] => r.rows) : null;
  const topCustomersPromise = canReadReports ? topCustomers(from30, to, can(permissionState, "customers.read")) : null;
  const stalenessPromise = can(permissionState, "pricing.read") ? getTierStalenessReport() : null;
  const variancePromise = can(permissionState, "packing.oversight") ? getVarianceReport() : null;
  const exceptionsPromise = can(permissionState, "inventory.read") ? getStockExceptions().then((r) => r.rows.length) : null;
  const agentPromise = can(permissionState, "integrations.read") ? getAgentStatus() : null;
  const queuePromise = can(permissionState, "integrations.read") ? getSyncQueue({ take: 1 }).then((q) => q.failedCount) : null;
  const refundsPromise = can(permissionState, "billing.read") ? getOutstandingRefunds().then((r) => r.refunds) : null;
  // Same code the route requires (`GET /customers/admin/customers/credit-breaches`).
  const creditBreachesPromise = can(permissionState, "customers.credit") ? getCreditBreaches().then((r) => r.count) : null;
  // Status 0 is `pending_verification` (`CustomerStatus.cs`); the same read `/customers?status=0` makes.
  const pendingCustomersPromise = can(permissionState, "customers.read") ? listCustomers({ page: 1, pageSize: 5, status: "0" }) : null;
  // The same code every `api/identity/invitations` route requires (`pages/invitations/List.tsx`).
  const invitationsPromise = can(permissionState, "company.user.invite") ? listInvitations() : null;

  // `TileBoundary`'s <Await> attaches its own rejection handler only once each tile mounts, a tick
  // after this loader returns — silence the "unhandled rejection" warning that gap would otherwise
  // produce for every tile whose service can reject. A no-op catch on a second reference doesn't
  // stop `Await`'s own handler from firing; promises support any number of independent subscribers.
  for (const p of [
    ordersPromise,
    pipelinePromise,
    orderTrendPromise,
    invoicedDailyPromise,
    invoicedMonthlyPromise,
    topSkusPromise,
    topCustomersPromise,
    stalenessPromise,
    variancePromise,
    exceptionsPromise,
    agentPromise,
    queuePromise,
    refundsPromise,
    creditBreachesPromise,
    pendingCustomersPromise,
    invitationsPromise,
  ]) {
    p?.catch(() => {});
  }

  return {
    permissionState,
    loadedAt: Date.now(),
    ordersPromise,
    pipelinePromise,
    orderTrendPromise,
    invoicedDailyPromise,
    invoicedMonthlyPromise,
    topSkusPromise,
    topCustomersPromise,
    stalenessPromise,
    variancePromise,
    exceptionsPromise,
    agentPromise,
    queuePromise,
    refundsPromise,
    creditBreachesPromise,
    pendingCustomersPromise,
    invitationsPromise,
  };
}

export type DashboardLoaderData = {
  permissionState: PermissionState;
  /** When this loader ran — the "Updated" stamp, so a reader knows how old every figure is. */
  loadedAt: number;
  ordersPromise: Promise<PagedResult<OrderSummary>> | null;
  pipelinePromise: Promise<Record<string, number | string>> | null;
  orderTrendPromise: Promise<OrderTrendReport> | null;
  invoicedDailyPromise: Promise<SalesTrendReport> | null;
  invoicedMonthlyPromise: Promise<SalesTrendReport> | null;
  topSkusPromise: Promise<TopRow[]> | null;
  topCustomersPromise: Promise<TopRow[]> | null;
  stalenessPromise: Promise<TierStalenessReport> | null;
  variancePromise: Promise<VarianceReportView> | null;
  exceptionsPromise: Promise<number> | null;
  agentPromise: Promise<AgentStatus> | null;
  queuePromise: Promise<number | null> | null;
  refundsPromise: Promise<OutstandingRefund[]> | null;
  creditBreachesPromise: Promise<number> | null;
  pendingCustomersPromise: Promise<PagedCustomers> | null;
  invitationsPromise: Promise<InvitationList> | null;
};

/** A titled band of the dashboard: heading and a one-line reading guide over its own grid. */
function Section({ id, title, note, children }: { id: string; title: string; note: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="@4xl:col-span-2 @7xl:col-span-12">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id={id} className="font-lexend text-lg font-semibold text-gray-900">
          {title}
        </h2>
        <p className="text-sm text-gray-500">{note}</p>
      </div>
      {children}
    </section>
  );
}

export default function DashboardPage() {
  const { account, permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const data = useLoaderData() as DashboardLoaderData;
  const canReadOrders = can(permissionState, "orders.read");
  const pending = pendingNav(permissionState.permissions);
  const greetingName = account.displayName ?? account.email ?? "there";
  const revalidator = useRevalidator();
  const refreshing = revalidator.state === "loading";

  // Without the tall status column, the banner and stat row take the full width rather than
  // leaving the template's right-hand four columns empty.
  const mainSpan = data.pipelinePromise ? "@4xl:col-span-2 @7xl:col-span-8" : "@4xl:col-span-2 @7xl:col-span-12";
  const hasStats = Boolean(data.orderTrendPromise || data.invoicedDailyPromise);
  const attention = [
    data.pipelinePromise ? <AwaitingApprovalCard key="approval" resolve={data.pipelinePromise} /> : null,
    data.stalenessPromise ? <StaleTierPricesTile key="staleness" resolve={data.stalenessPromise} /> : null,
    data.exceptionsPromise ? <StockExceptionsTile key="exceptions" resolve={data.exceptionsPromise} /> : null,
    data.variancePromise ? (
      <PackingVarianceTile key="variance" resolve={data.variancePromise} showReportLink={can(permissionState, "packing.oversight")} />
    ) : null,
    data.creditBreachesPromise ? <CreditBreachTile key="breaches" resolve={data.creditBreachesPromise} /> : null,
    data.refundsPromise ? <OutstandingRefundsTile key="refunds" resolve={data.refundsPromise} /> : null,
    data.queuePromise ? <FailedSyncsTile key="queue" resolve={data.queuePromise} /> : null,
    data.agentPromise ? <AgentHeartbeatTile key="agent" resolve={data.agentPromise} /> : null,
  ].filter(Boolean);
  const waiting = [
    data.pendingCustomersPromise ? <PendingCustomersTile key="customers" resolve={data.pendingCustomersPromise} /> : null,
    data.invitationsPromise ? <PendingInvitationsTile key="invitations" resolve={data.invitationsPromise} /> : null,
    data.ordersPromise ? (
      <div key="orders" className="@4xl:col-span-2 [&>*]:h-full">
        <PendingApprovalsTile resolve={data.ordersPromise} />
      </div>
    ) : null,
  ].filter(Boolean);
  const sales = [
    data.invoicedMonthlyPromise ? (
      <div key="monthly" className="@4xl:col-span-2 [&>*]:h-full">
        <InvoicedByMonthTile resolve={data.invoicedMonthlyPromise} />
      </div>
    ) : null,
    data.topSkusPromise ? (
      <TopSellersTile key="skus" title="Top SKUs" empty="No SKUs invoiced yet" emptyIcon={<Package />} period={RECENT} resolve={data.topSkusPromise} />
    ) : null,
    data.topCustomersPromise ? (
      <TopSellersTile key="customers" title="Top customers" empty="No customers invoiced yet" emptyIcon={<Users />} period={RECENT} resolve={data.topCustomersPromise} />
    ) : null,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader title="Dashboard" breadcrumb={[{ name: "Dashboard" }]}>
        {/* Every figure below is as of the loader's run; Refresh re-runs it (each tile refetches on
            its own, so a slow service still holds up only its own tile). */}
        <div className="mt-4 flex items-center gap-3 @lg:mt-0">
          <span className="text-sm text-gray-500" aria-live="polite">
            {refreshing ? "Refreshing…" : `Updated ${UPDATED.format(data.loadedAt)}`}
          </span>
          <Button variant="outline" size="sm" onClick={() => revalidator.revalidate()} disabled={refreshing}>
            <Refresh aria-hidden className={`me-1.5 size-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </PageHeader>

      <div className="@container">
        {/* `grid-flow-row-dense` lets the tall status card sit beside the banner and stat row at the
            widest size without the template's fixed row numbers, which would break the moment a
            permission hides a tile above it. Below that width it spans the full row. */}
        <div className="grid grid-flow-row-dense grid-cols-1 gap-6 @4xl:grid-cols-2 @7xl:grid-cols-12 3xl:gap-8">
          <WelcomeBanner
            title={
              <>
                Good to see you, <br />
                {greetingName} <HandWaveIcon className="inline-flex h-8 w-8" />
              </>
            }
            description={`${TODAY.format(new Date())}. What came in, where it stands, and what needs you.`}
            media={
              <div className="absolute bottom-5 end-6 hidden w-[300px] @2xl:block lg:w-[330px]">
                {/* The stock the business sells, drawn as sections — not the template's shopping bags. */}
                <img src="/steel-sections.svg" alt="" className="w-full dark:opacity-90" />
              </div>
            }
            contentClassName="@2xl:max-w-[calc(100%-340px)]"
            className={`border border-muted bg-gray-0 pb-8 dark:bg-gray-100/30 lg:pb-9 ${mainSpan}`}
          >
            <div className="flex flex-wrap gap-3">
              {canReadOrders ? (
                <Link to="/orders" className="inline-flex">
                  <Button as="span" className="h-[38px] shadow md:h-10">
                    View orders
                  </Button>
                </Link>
              ) : null}
              {can(permissionState, "billing.read") ? (
                <Link to="/reports/sales" className="inline-flex">
                  <Button as="span" variant="outline" className="h-[38px] md:h-10">
                    Sales report
                  </Button>
                </Link>
              ) : null}
            </div>
          </WelcomeBanner>

          {hasStats ? (
            <div className={`grid grid-cols-1 gap-5 3xl:gap-8 4xl:gap-9 @2xl:grid-cols-3 @3xl:gap-6 ${mainSpan}`}>
              {data.orderTrendPromise ? <OrderTrendCards resolve={data.orderTrendPromise} period={RECENT} /> : null}
              {data.invoicedDailyPromise ? <InvoicedCard resolve={data.invoicedDailyPromise} period={RECENT} /> : null}
            </div>
          ) : null}

          {data.pipelinePromise ? (
            <div className="@4xl:col-span-2 @7xl:col-span-4 @7xl:col-start-9 @7xl:row-start-1 @7xl:row-end-3">
              <OrdersByStatusTile resolve={data.pipelinePromise} className="h-full" />
            </div>
          ) : null}

          {waiting.length > 0 ? (
            <Section id="waiting-heading" title="Waiting on you" note="Registrations, invitations and orders no one has acted on yet.">
              <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-2 3xl:gap-8 [&>*]:min-w-0">{waiting}</div>
            </Section>
          ) : null}

          {attention.length > 0 ? (
            <Section id="operations-heading" title="Operations" note="Green is clear. Amber or red needs someone; each card links to where you fix it.">
              <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @6xl:grid-cols-4 [&>*]:h-full">{attention}</div>
            </Section>
          ) : null}

          {sales.length > 0 ? (
            <Section id="sales-heading" title="Sales" note={`Invoiced value by month, and the top five over the ${RECENT.toLowerCase()}.`}>
              <div className="grid grid-cols-1 gap-6 @4xl:grid-cols-2 3xl:gap-8 [&>*]:h-full [&>*]:min-w-0">{sales}</div>
            </Section>
          ) : null}

          {pending.length > 0 ? (
            <div className="@4xl:col-span-2 @7xl:col-span-12">
              <WidgetCard
                title={
                  <>
                    <Construction aria-hidden className="text-muted-foreground size-4" />
                    Not available yet
                  </>
                }
                headerClassName="border-muted border-b"
                description="You have permission for these. They're listed here rather than in the navigation, so a missing menu item always means “not permitted”."
              >
                <ul className="flex flex-col pt-4">
                  {pending.map((entry) => (
                    <li key={entry.href} className="border-muted flex flex-col gap-1 border-b py-3 last:border-0 last:pb-0 first:pt-0 sm:flex-row sm:items-baseline sm:gap-4">
                      <span className="flex items-center gap-2 text-sm font-medium sm:w-40 sm:shrink-0">
                        <entry.icon aria-hidden className="text-muted-foreground size-4" />
                        {entry.label}
                      </span>
                      <span className="text-muted-foreground text-sm">{entry.pendingReason}</span>
                    </li>
                  ))}
                </ul>
              </WidgetCard>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
